import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import selectOnFocus from '../../../../../modules/autoselect'

type Props = {
  value: string | number,
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const TableCellInput = ({
  value,
  onChange
}: Props) => {
  const [localValue, setLocalValue] = useState<string>('')
  const [isFocused, setIsFocused] = useState(false)

  // Sync external value to local state when not focused
  useEffect(() => {
    if (!isFocused) {
      // Hide 0 values - show empty string
      setLocalValue(value === 0 || value === '' ? '' : String(value))
    }
  }, [value, isFocused])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    
    // Allow empty string, numbers, and partial decimal inputs like "0.", "."
    if (newValue === '' || newValue === '.' || !isNaN(Number(newValue))) {
      setLocalValue(newValue)
      onChange(e)
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    // If the cell was empty (0 value), set local value to empty for typing
    if (value === 0 || value === '') {
      setLocalValue('')
    } else {
      setLocalValue(String(value))
    }
    selectOnFocus(e)
  }

  const handleBlur = () => {
    setIsFocused(false)
    // No need to format here - just let the parent component handle the value
  }

  const displayValue = isFocused ? localValue : (value === 0 || value === '' ? '' : value)

  return (
    <Input 
      type='text'
      inputMode='decimal'
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  )
}
const Input = styled.input`
  width: 100%;
  height: 32px;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radii.sm};
  background-color: transparent;
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  outline: none;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorder};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.accentDeep};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`
export default TableCellInput